import React from 'react';
import _ from 'underscore';

import EntryActions from 'actions/EntryActions';
import * as analytics from 'analytics/Analytics';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import CloneModal from 'folder-items/CloneModal';
import {Entry, FromServerId, VersionSummary} from 'notebook/ELNConstants';
import ReviewConstants from 'notebook/reviews/ReviewConstants';
import {ItemVersionState} from 'notebook/store/VersionedItemSubstore';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import getAllStructuredTablesInNote from 'notebook/structured-tables/util/getAllStructuredTablesInNote';
import VersionHistoryPanel from 'notebook/version-history/VersionHistoryPanel';
import {Version} from 'notebook/version-history/VersionHistoryTypes';
import EntryStore from 'stores/EntryStore';
import legacyRunModal from 'ui-library/legacyRunModal';

const fileStates = require('FileStates').default.get();

class EntryHistoryPanelConnector extends DataConnector<Props, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [EntryStore];

  enhanceProps(): EnhancedProps {
    return {versionState: this.entryStore().getVersionState(this.props.id)};
  }
}

interface Props {
  id: string;
  folderId?: string;
  name: string;
  permissions: Permissions;
}
interface EnhancedProps {
  versionState: ItemVersionState<VersionSummary<FromServerId>>;
}

class EntryHistoryPanel extends React.Component<Props & EnhancedProps> {
  constructor(props: Props & EnhancedProps) {
    super(props);
  }

  override render(): JSX.Element {
    const versions: Array<VersionSummary<FromServerId>> = this.props.versionState.versions.toReversed();

    const entry = EntryStore.getEntry(this.props.id)!;
    const isLockedForReview =
      entry.review != null && ReviewConstants.LOCKED_STATUSES.includes(entry.review.status);
    const constainsProcessTables = containsProcessRelatedTables(entry);
    const disableResetToVersion =
      BenchlingConfig.ELN_DISABLE_ENTRY_REVERTS ||
      this.props.versionState.isLatest ||
      this.props.versionState.unsaved ||
      this.props.versionState.dirty ||
      !this.props.permissions.writable ||
      constainsProcessTables ||
      isLockedForReview;
    const disableDuplicateFromVersion = constainsProcessTables;
    return (
      <VersionHistoryPanel
        // TODO(BNCH-121327): FromServerEntry has `modified_at` and `created_at` instead of `modifiedAt` and
        // `createdAt`
        versions={versions as unknown as Array<Version>}
        // @ts-expect-error: openVersionId can be defined as a literal false
        openVersionId={this.props.versionState.openVersionId}
        navigateAndSetOpenVersionId={this.openVersion}
        enableResetToVersion={!disableResetToVersion}
        resetToVersion={disableResetToVersion ? () => {} : this.resetToVersion}
        enableDuplicateFromVersion={!disableDuplicateFromVersion}
        duplicateFromVersion={disableDuplicateFromVersion ? () => {} : this.cloneFromVersion}
        versionParent={this.props.versionState.parent ?? undefined}
        navigateToVersionParent={this.navigateToVersionParent}
        unsaved={this.props.versionState.unsaved || this.props.versionState.dirty}
      />
    );
  }

  override componentDidMount(): void {
    EntryActions.fetchVersions(this.props.id);
  }

  override componentDidUpdate(prevProps: Props & EnhancedProps): void {
    if (
      prevProps.versionState.savedVersion !== this.props.versionState.savedVersion ||
      prevProps.id !== this.props.id
    ) {
      // Avoid flux nested-dispatch issues
      _.defer(() => {
        EntryActions.fetchVersions(this.props.id);
      });
    }
  }

  openVersion = (versionId: number): void => {
    analytics.trackEvent(ELN_ANALYTICS_ACTIONS.OPENED_OLD_ENTRY_VERSION, {entry_id: this.props.id});
    EntryActions.loadVersion(this.props.id, versionId);
  };

  resetToVersion = (): void => {
    analytics.trackEvent(ELN_ANALYTICS_ACTIONS.REVERTED_ENTRY_VERSION, {entry_id: this.props.id});
    EntryActions.resetToOpenVersion(this.props.id);
  };

  navigateToVersionParent = (): void => {
    const parent = this.props.versionState.parent;
    if (parent) {
      fileStates.openFolderItem(parent.id, {
        open: true,
        source: 'history-panel-component',
        versionId: parent.versionId,
      });
    }
  };

  cloneFromVersion = (): void => {
    legacyRunModal(CloneModal, {
      modelType: ModelTypeConstants.ENTRY,
      id: this.props.id,
      // @ts-expect-error: openVersionId can be defined as a literal false
      versionId: this.props.versionState.openVersionId!,
      initialName: `${this.props.name} (new)`,
      initialFolderId: this.props.folderId,
    });
  };
}

export default EntryHistoryPanelConnector.connect(EntryHistoryPanel);

function containsProcessRelatedTables(entry: Entry): boolean {
  if (entry.note != null) {
    const structuredTables = getAllStructuredTablesInNote(entry.note);
    return structuredTables.some(
      (structuredTable) =>
        structuredTable.kind === StructuredTableKind.PROCESS_TASK ||
        structuredTable.kind === StructuredTableKind.PROCESS_OUTPUT
    );
  }
  return false;
}
